/**
 * MyArFollowups — Dashboard widget showing my open AR follow-up tasks
 * with overdue counter + quick "Mark done" / "Open customer" actions.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ListChecks, WarningCircle, Check, ArrowSquareOut } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { myArFollowupsWidget, updateArFollowup } from '../lib/api';

function relTime(iso) {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(ms);
  const m = Math.floor(abs / 60000);
  if (m < 60) return `${ms < 0 ? '-' : ''}${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${ms < 0 ? '-' : ''}${h}h`;
  const d = Math.floor(h / 24);
  return `${ms < 0 ? '-' : ''}${d}d`;
}

export default function MyArFollowups() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);

  const refresh = async () => {
    try {
      const r = await myArFollowupsWidget();
      setData(r.data);
    } catch (_) {
      setData(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  // surface overdue toast on first load
  useEffect(() => {
    if (data && data.overdue_count > 0) {
      const KEY = '__ar_overdue_toast_shown';
      if (!sessionStorage.getItem(KEY)) {
        toast.warning(`You have ${data.overdue_count} overdue AR follow-up${data.overdue_count === 1 ? '' : 's'}`, {
          description: 'Check the AR Follow-ups widget on your dashboard.',
          duration: 6000,
        });
        sessionStorage.setItem(KEY, '1');
      }
    }
  }, [data]);

  const markDone = async (id) => {
    setCompleting(id);
    try {
      await updateArFollowup(id, { status: 'done' });
      toast.success('Follow-up marked done');
      await refresh();
    } catch (e) {
      toast.error('Could not update');
    } finally { setCompleting(null); }
  };

  if (loading) {
    return <div className="bg-white border border-zinc-200 p-4 text-xs italic text-zinc-400" data-testid="ar-widget-loading">Loading AR follow-ups…</div>;
  }
  if (!data || data.open_count === 0) {
    return (
      <div className="bg-white border border-zinc-200 p-4" data-testid="ar-widget-empty">
        <div className="flex items-center gap-2 mb-2">
          <ListChecks size={14} weight="bold" className="text-violet-600" />
          <h3 className="text-xs font-bold uppercase tracking-widest">My AR follow-ups</h3>
        </div>
        <p className="text-xs text-zinc-400 italic">No open follow-ups. Schedule one from the Customers page.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-zinc-200" data-testid="ar-widget">
      <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
        <h3 className="text-xs font-bold uppercase tracking-widest flex items-center gap-2">
          <ListChecks size={14} weight="bold" className="text-violet-600" />
          My AR follow-ups
        </h3>
        <div className="flex items-center gap-2">
          {data.overdue_count > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase border bg-rose-50 border-rose-300 text-rose-700" data-testid="ar-widget-overdue-badge">
              <WarningCircle size={10} weight="bold" /> {data.overdue_count} overdue
            </span>
          )}
          <span className="text-[11px] text-zinc-500">{data.open_count} open</span>
          <Link to="/ar/followups" className="text-[11px] font-bold text-violet-700 hover:underline" data-testid="ar-widget-view-all">View all →</Link>
        </div>
      </div>
      <ul className="divide-y divide-zinc-100">
        {data.upcoming.map((t) => {
          const overdue = new Date(t.due_date).getTime() < Date.now();
          return (
            <li key={t.id} className="px-4 py-2 flex items-start gap-2" data-testid={`ar-widget-item-${t.id}`}>
              <button
                disabled={completing === t.id}
                onClick={() => markDone(t.id)}
                className="w-5 h-5 flex items-center justify-center border border-zinc-300 hover:bg-emerald-50 hover:border-emerald-400 mt-0.5 shrink-0"
                title="Mark done"
                data-testid={`ar-widget-done-${t.id}`}
              >
                <Check size={11} weight="bold" className="text-emerald-700" />
              </button>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold truncate">{t.title}</div>
                <div className="text-[10px] text-zinc-500 flex flex-wrap gap-1.5">
                  <span className={overdue ? 'text-rose-700 font-bold' : ''}>
                    Due {new Date(t.due_date).toLocaleDateString()} · {relTime(t.due_date)}
                  </span>
                  {t.customer_name && <span>· {t.customer_name}</span>}
                  {t.invoice_number && <span>· {t.invoice_number}</span>}
                </div>
              </div>
              {t.customer_email && (
                <Link
                  to={`/invoices/customer/${encodeURIComponent(t.customer_email)}`}
                  className="text-[10px] font-bold text-zinc-500 hover:text-violet-700 inline-flex items-center gap-0.5"
                  title="Open customer"
                  data-testid={`ar-widget-open-${t.id}`}
                >
                  <ArrowSquareOut size={10} />
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
